import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { __RouterContext as RouterContext } from "react-router"; // v5; not re-exported by -dom
import Button from "./Button/Button.js";
import { useNavItems, findNavItem, nodeText } from "../Util/navRegistry.js";

/**
 * Top toolbar with an optional back arrow and a page title.
 *
 * Wayfinding: names the browser tab "<title> · Virangi" and shows a small
 * "Group › Section" eyebrow from the menu (Util/navRegistry.js); on a sub-page
 * (add / details / update) the section links back to its list page.
 *
 * Props:
 *   title    – string         — page heading, rendered as the page's <h1>
 *   onBack   – () => void     — if provided, renders a ← button
 *   actions  – node           — optional right-side slot (e.g. a button)
 *   docsSlug – string         — Pages/Docs article id; adds a "?" help link
 */
function PageToolbar({ title, onBack, actions, docsSlug }) {
    // Router context read directly so the toolbar still renders outside a Router (tests)
    const pathname = useContext(RouterContext)?.location?.pathname || "";
    const navItems = useNavItems();
    const entry = pathname ? findNavItem(pathname, navItems) : null;
    const titleText = nodeText(title).trim();

    useEffect(() => {
        if (!titleText) return undefined;
        document.title = `${titleText} · Virangi`;
        return () => { document.title = "Virangi"; };
    }, [titleText]);

    const onListPage = entry && pathname.replace(/\/+$/, "") === entry.to.replace(/\/+$/, "");
    // "Sales Bill" menu item vs "Sales Bills" heading is the same page name
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/s\b/g, "");
    const sameName = entry && norm(entry.label) === norm(titleText);

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                marginBottom: "1rem",
                flexWrap: "wrap",          /* allows wrapping on very narrow screens */
                rowGap: "8px",
            }}
        >
            {/* ── Left: back arrow + title ── */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
                {onBack && (
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={onBack}
                        title="Go back"
                        style={{
                            flexShrink: 0,
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            background: "#fff3f1",
                            border: "1px solid #f0c4be",
                            borderRadius: "6px",
                            color: "#c84535",
                            fontSize: "var(--fs-section)",
                        }}
                    >
                        <i className="bi bi-arrow-left" />
                    </Button>
                )}

                <div style={{ minWidth: 0 }}>
                {entry && (
                    <div
                        className="page-toolbar-eyebrow"
                        style={{
                            fontSize: "var(--fs-hint)",
                            color: "var(--ink-muted)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: 1.3,
                        }}
                    >
                        {entry.trail.join(" › ")}
                        {!sameName && (
                            <>
                                {" › "}
                                {onListPage
                                    ? entry.label
                                    : <Link to={entry.to} style={{ color: "inherit", textDecoration: "underline", textUnderlineOffset: 2 }}>{entry.label}</Link>}
                            </>
                        )}
                    </div>
                )}
                {/* The page's one real heading (WCAG 1.3.1), styled as the old h5. */}
                <h1
                    style={{
                        margin: 0,
                        fontWeight: 700,
                        fontSize: "var(--fs-title)", /* token: 1.15rem desktop, 18px phone */
                        lineHeight: 1.2,
                        letterSpacing: "var(--ls-title)",
                        color: "#1a1a1a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                    }}
                >
                    {title}
                </h1>
                </div>
                {/* Help for this page, where a docs article exists */}
                {docsSlug && (
                    <Link
                        to={`/docs?article=${encodeURIComponent(docsSlug)}`}
                        className="page-toolbar-help"
                        aria-label={`Help: ${titleText || "this page"}`}
                        title="How this page works"
                    >
                        <i className="bi bi-question-circle" aria-hidden="true" />
                    </Link>
                )}
            </div>

            {/* ── Right: action slot ── */}
            {/* May shrink and wrap (App.css .page-toolbar-actions): on phones the
                buttons drop under the title and wrap instead of widening the page. */}
            {actions && (
                <div className="page-toolbar-actions" style={{ flexShrink: 1, minWidth: 0, maxWidth: "100%" }}>
                    {actions}
                </div>
            )}
        </div>
    );
}

export default PageToolbar;